import {
  Body1,
  Box,
  Button,
  Card,
  Container,
  Display2,
  Grid,
  GridItem,
  Link,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@sainsburys-tech/fable'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getSkus } from '../../../common/api/skuApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import ScrollToPrevious from '../../../common/components/ScrollToPrevious'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { ISku } from '../../../common/types/ISku'
import { getUserStore, setCurrentPlanner } from '../../../utils/localStore'

const QpigListView = () => {
  const { dispatch } = useContext(Context)
  const navigate = useNavigate()
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [skus, setSkus] = useState<ISku[]>([])
  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.Bakery,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  const scrollToTop = () => {
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    const loadSkusData = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [skusData] = await Promise.all([
          getSkus(apiClient, store.storeId),
        ])

        setSkus(skusData)
      } catch (e) {
        handleError(ErrorMessage.QpigView.FailedToLoad, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadSkusData()
  }, [apiClient, store.storeId, handleError, resetError])

  const openQpig = (skuId: number, e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setCurrentPlanner('bakery')
    navigate(`/qpig/${skuId}`)
  }

  const search = searchInput.trim().toLowerCase()
  const filteredSkus = skus.filter(
    (sku) =>
      sku.skuName.toLowerCase().includes(search) ||
      sku.skuId.toString().includes(search),
  )
  const getSku = () =>
    filteredSkus
      .sort((a, b) => (a.skuName.trim() > b.skuName.trim() ? 1 : -1))
      .map((skuValue, i) => {
        const getSkuId = skuValue.skuId
        const getSkuName = skuValue.skuName
        return (
          <TableRow data-testid='qpig-listview-row' key={i + 1}>
            <TableCell>
              <Link
                className='c-common-hover-over-cursor c-qpig-link-underline'
                href=''
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  openQpig(getSkuId, e)
                }
                testid='qpig-link'
              >
                <Body1>{`${getSkuName} (${getSkuId})`}</Body1>
              </Link>
            </TableCell>
          </TableRow>
        )
      })

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      {!isLoading && (
        <Box padding='none' testid='qpig-list-view'>
          <Header
            title={headerPlannerTitles.bakery}
            navItems={headerNavigationItems}
            activeMenuType={Menu.Qpig}
          />

          <Container className='c-common-main-view-content'>
            {!isLoading && errorMessage && (
              <ErrorRefresh message={errorMessage} />
            )}

            {!isLoading && !errorMessage && (
              <div>
                <Display2
                  style={{ marginBottom: '1.5rem', marginTop: '1.0rem' }}
                >
                  Qpigs
                </Display2>
                <Card orientation='vertical'>
                  <div>
                    <Grid>
                      <GridItem colSpan={12}>
                        <SearchInput
                          name='searchField'
                          label='Search Qpigs'
                          button={false}
                          placeholder='Enter product name or SKU number'
                          leadingIcon
                          value={searchInput}
                          onChange={(value: string) => {
                            setSearchInput(value)
                          }}
                          testid='qpig-search-input'
                        />
                      </GridItem>
                      <GridItem colSpan={12}>
                        <ScrollToPrevious scrollAreaId='qpig-list-view-scroll-area'>
                          <Table testid='qpig-listview-table'>
                            <TableBody className='tableBody'>
                              {getSku()}
                            </TableBody>
                          </Table>
                        </ScrollToPrevious>
                      </GridItem>
                    </Grid>
                  </div>
                  <div>
                    <Button
                      data-testid='return-to-top'
                      onClick={() => scrollToTop()}
                      testid='return-to-top-button'
                    >
                      Return to top
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </Container>
        </Box>
      )}
    </>
  )
}

export default QpigListView
